package com.evoapps.evoshift.documents

import androidx.compose.runtime.Composable
import androidx.compose.runtime.remember
import androidx.compose.runtime.rememberUpdatedState
import com.evoapps.evoshift.domain.usecase.documents.PickedFile
import kotlinx.browser.document
import kotlinx.browser.window
import org.khronos.webgl.ArrayBuffer
import org.khronos.webgl.Int8Array
import org.khronos.webgl.Uint8Array
import org.w3c.dom.HTMLAnchorElement
import org.w3c.dom.HTMLInputElement
import org.w3c.dom.url.URL
import org.w3c.files.Blob
import org.w3c.files.BlobPropertyBag
import org.w3c.files.FileReader
import org.w3c.files.get

/** The browser's file chooser (a hidden file input), opened by the click that asked for it. */
@Composable
internal actual fun rememberPlatformDocumentPicker(onPicked: OnDocumentPicked): DocumentPicker {
    val callback = rememberUpdatedState(onPicked)
    return remember {
        object : DocumentPicker {
            override fun pick(kind: PickKind) {
                val input = document.createElement("input") as HTMLInputElement
                input.type = "file"
                input.accept = (pickMimeTypes(kind) + pickExtensions(kind).map { ".$it" }).joinToString(",")
                input.style.display = "none"
                input.onchange = {
                    val file = input.files?.get(0)
                    input.remove()
                    if (file != null) {
                        val reader = FileReader()
                        reader.onload = {
                            val bytes = Int8Array(reader.result.unsafeCast<ArrayBuffer>()).unsafeCast<ByteArray>()
                            callback.value(PickedFile(file.name, bytes))
                        }
                        reader.onerror = { callback.value(null) }
                        reader.readAsArrayBuffer(file.slice(0, pickReadLimit(kind)))
                    }
                }
                input.addEventListener("cancel", { input.remove() })
                document.body?.appendChild(input)
                input.click()
            }
        }
    }
}

/**
 * Hands the document to the browser as a download (its PDF viewer or the downloads bar opens it);
 * a pop-up tab would be blocked, as the file arrives after the click.
 */
class WebDocumentOpener : DocumentOpener {
    override fun open(fileName: String, contentType: String, bytes: ByteArray) {
        // Name and type from the bytes, not the stored name (SH-177).
        val openable = openableDocument(fileName, bytes)
        val data = Uint8Array(bytes.unsafeCast<Int8Array>().buffer)
        val blob = Blob(arrayOf(data), BlobPropertyBag(type = openable.contentType))
        val url = URL.createObjectURL(blob)
        val link = document.createElement("a") as HTMLAnchorElement
        link.href = url
        link.download = openable.fileName
        link.style.display = "none"
        document.body?.appendChild(link)
        link.click()
        link.remove()
        window.setTimeout({ URL.revokeObjectURL(url) }, 60_000)
    }
}
